import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { isPaidPlan } from "@/lib/trial";

type CancelReason =
  | "too_expensive"
  | "not_using_enough"
  | "missing_features"
  | "found_alternative"
  | "temporary_break"
  | "other";

const REASONS: ReadonlyArray<{ id: CancelReason; label: string }> = [
  { id: "not_using_enough", label: "No longer need it" },
  { id: "missing_features", label: "Lack of functionality" },
  { id: "too_expensive", label: "Too expensive" },
  { id: "found_alternative", label: "Found another tool" },
  { id: "temporary_break", label: "Purchased accidentally" },
  { id: "other", label: "Other (please specify)" },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CancelPlanDialog({ open, onOpenChange }: Props) {
  const { user, refetch } = useAuth();
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [endsAt, setEndsAt] = useState<string | null>(null);
  const [scheduledViaStripe, setScheduledViaStripe] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(null);
      setComment("");
      setError(null);
      setDone(false);
      setSubmitting(false);
      setEndsAt(null);
      setScheduledViaStripe(false);
    }
  }, [open]);

  const onPaidPlan = isPaidPlan(user?.plan);

  async function handleSubmit() {
    if (!reason) {
      setError("Please select a reason before continuing.");
      return;
    }
    if (reason === "other" && !comment.trim()) {
      setError("Please tell us a bit more when choosing Other.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl("/billing/cancel"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, comment: comment.trim() || undefined }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      const body = (await res.json().catch(() => ({}))) as {
        cancellation?: { scheduled?: boolean; endsAt?: string | null };
      };
      setEndsAt(body.cancellation?.endsAt ?? null);
      setScheduledViaStripe(Boolean(body.cancellation?.scheduled));
      setDone(true);
      refetch();
    } catch (err) {
      setError((err as Error).message ?? "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const friendlyDate = endsAt
    ? new Date(endsAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="z-[100]"
        className="z-[101] sm:max-w-[480px] rounded-2xl border border-black/10 bg-background p-0 gap-0 shadow-xl overflow-hidden"
      >
        <div className="px-6 pt-6 pb-2">
          <DialogHeader className="space-y-3 text-left">
            <DialogTitle className="font-display text-xl font-semibold text-foreground">
              {done ? "Cancellation scheduled" : "Cancel Plan"}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground text-left">
              {done
                ? scheduledViaStripe && friendlyDate
                  ? `Your subscription won't renew. You'll keep full access until ${friendlyDate}, then your account moves to Starter. You can resume anytime before then from the pricing page.`
                  : "Your account is back on Starter. Your journals stay yours — you can upgrade again anytime."
                : "Thank you for choosing Unfold. We're sorry to see you go. Before you cancel, please select a reason from the options."}
            </DialogDescription>
          </DialogHeader>
        </div>

        {!done && (
          <div className="px-6 pb-2 space-y-4">
            <fieldset className="space-y-0 divide-y divide-black/[0.06] rounded-xl border border-black/[0.08] overflow-hidden bg-white/50">
              {REASONS.map((r) => {
                const selected = reason === r.id;
                return (
                  <label
                    key={r.id}
                    className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors ${
                      selected ? "bg-primary/[0.04]" : "hover:bg-muted/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancel-reason"
                      value={r.id}
                      checked={selected}
                      onChange={() => setReason(r.id)}
                      className="h-4 w-4 accent-primary shrink-0"
                    />
                    <span className="text-sm text-foreground">{r.label}</span>
                  </label>
                );
              })}
            </fieldset>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Reason for cancellation…"
              className="w-full rounded-xl border border-black/[0.08] bg-muted/30 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/25 resize-none"
            />

            {error && (
              <p className="rounded-xl border border-red-200/70 bg-red-50/70 px-4 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="flex-row justify-end gap-2 border-t border-black/[0.06] bg-muted/10 px-6 py-4 sm:justify-end">
          {done ? (
            <Button
              className="rounded-full px-6 h-10 text-sm font-medium"
              onClick={() => onOpenChange(false)}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                className="rounded-full px-5 h-10 text-sm font-medium bg-muted/50 border-black/10"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Dismiss
              </Button>
              <Button
                className="rounded-full px-5 h-10 text-sm font-medium"
                onClick={handleSubmit}
                disabled={submitting || !reason}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing…
                  </span>
                ) : onPaidPlan ? (
                  "Next"
                ) : (
                  "Submit"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
